import React, { useEffect, useRef, useState } from 'react';

import { Table as AntdTable, TablePaginationConfig } from 'antd';

import replacePaginationElements from './utils/replacePaginationElements';

import { DefaultPagination } from 'constants/pagination';
import { DEFAULT_PAGINATION } from './constants/pagination';

import { ITableProps } from './Table.types';

import './Table.styles.scss';

const Table = <T extends object>({
  className,
  bordered,
  rounded,
  baseline,
  nested,
  pagination = {},
  dataSource = [],
  leftHeaderComponent,
  centerHeaderComponent,
  rightHeaderComponent,
  summary,
  onChange,
  ...props
}: ITableProps<T>) => {
  const DefaultScroll = { x: '100%', scrollToFirstRowOnChange: true };

  const [currentPage, setCurrentPage] = useState(DefaultPagination.START_PAGE);

  const tablePagination =
    pagination === false ? pagination : { ...DEFAULT_PAGINATION, current: currentPage, ...pagination };

  const { pageSize, defaultPageSize } = tablePagination || {};

  const showHeader = leftHeaderComponent || rightHeaderComponent || centerHeaderComponent;

  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  const [currentPageSize, setCurrentPageSize] = useState(defaultPageSize || pageSize || DefaultPagination.PAGE_LIMIT);

  const onTableChange = (newPagination: TablePaginationConfig, filters, sorter, extra) => {
    const page = newPagination.pageSize === currentPageSize ? newPagination?.current : DefaultPagination.START_PAGE;
    const newSorter = { ...sorter, sorterFieldName: sorter.column?.sorterFieldName || sorter.columnKey };

    if (onChange) {
      onChange({ ...newPagination, current: page }, filters, newSorter, extra);
    }

    setCurrentPageSize(newPagination?.pageSize);
    setCurrentPage(page);
  };

  useEffect(() => {
    if (dataSource?.length && tableWrapperRef.current) {
      replacePaginationElements(tableWrapperRef.current);
    }
  }, [dataSource]);

  return (
    <div ref={tableWrapperRef} className={`np-table__wrapper ${rounded ? 'np-table__wrapper--rounded' : ''}`}>
      {showHeader && (
        <div className="np-table__header">
          <div className="np-table__header-item">{leftHeaderComponent}</div>

          {centerHeaderComponent && <div className="np-table__header-item">{centerHeaderComponent}</div>}

          {rightHeaderComponent && <div className="np-table__header-item">{rightHeaderComponent}</div>}
        </div>
      )}

      <AntdTable
        bordered={bordered}
        dataSource={dataSource}
        summary={summary}
        sticky={summary ? { offsetHeader: 10 } : {}}
        pagination={tablePagination}
        scroll={dataSource.length ? { ...DefaultScroll, ...props.scroll } : {}}
        onChange={onTableChange}
        className={`np-table ${bordered && 'np-table__bordered'} ${baseline && 'np-table__baseline'} ${
          nested && 'np-table__nested'
        } ${className}`}
        {...props}
      />
    </div>
  );
};

Table.Summary = AntdTable.Summary;

export default Table;
